import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Plus, Pencil, Check, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";

function TestimonialRow({ t, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(t.text);
  const [name, setName] = useState(t.name);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/testimonials/${t.id}`, { text: text.trim(), name: name.trim() });
      toast.success("Testimonial updated.");
      setEditing(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async () => {
    try {
      await api.put(`/admin/testimonials/${t.id}`, { published: !t.published });
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      toast.success("Testimonial deleted.");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-5" data-testid={`testimonial-row-${t.id}`}>
      {editing ? (
        <div className="space-y-3">
          <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} aria-label="Quote text" className="w-full rounded-xl border-2 border-stone-300 px-3 py-2 text-base focus-visible:border-orange-600" data-testid={`testimonial-edit-text-${t.id}`} />
          <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Who said it" className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid={`testimonial-edit-name-${t.id}`} />
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-70" data-testid={`testimonial-save-${t.id}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
            </button>
            <button onClick={() => { setEditing(false); setText(t.text); setName(t.name); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-stone-300 text-sm font-semibold hover:bg-stone-100">
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-base text-stone-800">"{t.text}"</p>
          <p className="mt-2 text-sm font-semibold text-stone-600">— {t.name}</p>
          <div className="mt-3 flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-stone-700">
              <input type="checkbox" checked={t.published} onChange={togglePublished} className="h-4 w-4 accent-orange-600" data-testid={`testimonial-published-${t.id}`} />
              Shown on the website
            </label>
            <div className="flex gap-1">
              <button onClick={() => setEditing(true)} className="rounded-lg p-2 text-stone-500 hover:bg-orange-50 hover:text-orange-700" aria-label="Edit testimonial" data-testid={`testimonial-edit-${t.id}`}>
                <Pencil className="h-5 w-5" />
              </button>
              <button onClick={remove} className="rounded-lg p-2 text-stone-500 hover:bg-red-50 hover:text-red-700" aria-label="Delete testimonial" data-testid={`testimonial-delete-${t.id}`}>
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function TestimonialsTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ text: "", name: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/admin/testimonials", form);
      toast.success("Testimonial added.");
      setForm({ text: "", name: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="testimonials-tab">
      <div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">Add a testimonial</h3>
        <form onSubmit={add} className="mt-4 space-y-4 rounded-2xl bg-white border border-stone-200 p-5">
          <div>
            <label htmlFor="testimonial-text" className="block text-base font-medium text-stone-900 mb-1">Quote</label>
            <textarea id="testimonial-text" rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required className="w-full rounded-xl border-2 border-stone-300 px-3 py-2 focus-visible:border-orange-600" data-testid="testimonial-text-input" />
          </div>
          <div>
            <label htmlFor="testimonial-name" className="block text-base font-medium text-stone-900 mb-1">Who said it (e.g. "Parent of someone we support")</label>
            <input id="testimonial-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="testimonial-name-input" />
          </div>
          <button type="submit" disabled={adding} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-70" data-testid="testimonial-add-button">
            {adding ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
            Add testimonial
          </button>
        </form>
        <p className="mt-3 text-sm text-stone-500">Tip: only use quotes where the person has agreed to share their words.</p>
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">Current testimonials</h3>
        <div className="mt-4 space-y-3" data-testid="testimonials-admin-list">
          {items === null ? (
            <Loader2 className="h-5 w-5 animate-spin text-stone-500" />
          ) : items.length === 0 ? (
            <p className="text-stone-600">No testimonials yet.</p>
          ) : (
            items.map((t) => <TestimonialRow key={t.id} t={t} onChanged={load} />)
          )}
        </div>
      </div>
    </div>
  );
}
